import React from 'react';
import { SovAILogo } from 'assets/images';
import { FooterProps } from './types/Footer';
import { Link } from 'react-router-dom';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollToSection = (sectionId: string, offset = 0) => {
  const section = document.getElementById(sectionId);
  if (section) {
    const offsetPosition = section.offsetTop - offset;
    window.scrollTo({
      top: offsetPosition,
      // Honour the user's motion preference, matching the header's behaviour.
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }
};

// Shared focus treatment — instant, no transition. Mirrors the header.
const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-ground rounded-sharp';

// Footer links speak the same monospace voice as the nav.
const FOOTER_LINK =
  'no-underline font-mono text-[13px] lowercase tracking-[0.06em] ' +
  `text-muted hover:text-ink ${FOCUS_RING}`;

// Column headings: monospace, uppercase, wide-tracked — a fieldset legend, not
// a marketing header. `text-muted` (5.5:1) rather than `text-faint` (3.4:1),
// because these are small text and must clear WCAG AA.
const COLUMN_HEADING =
  'font-mono text-[11px] uppercase tracking-[0.18em] text-muted';

const linkData: FooterProps[] = [
  {
    title: 'Build with us',
    links: [
      {
        name: 'GitHub',
        url: 'https://github.com/sovai-research/sovai-public',
        new_tab: true,
      },
      {
        name: 'Application',
        url: 'https://sov.ai/app',
        new_tab: true,
      },
      { name: 'API Key', url: 'https://sov.ai/subscribe', new_tab: false },
      { name: 'Documentation', url: 'https://docs.sov.ai', new_tab: true },
    ],
  },
  {
    title: 'Product',
    links: [
      // The catalogue was absent from the footer as well as the nav.
      { name: 'Datasets', url: '/datasets', new_tab: false },
      { name: 'Blog', url: 'http://blog.sov.ai/', new_tab: true },
      {
        name: 'Features',
        url: '',
        new_tab: true,
        action: () => {
          scrollToSection('features');
        },
      },
      {
        name: 'Pricing',
        url: '',
        new_tab: true,
        action: () => {
          scrollToSection('pricing');
        },
      },
      { name: 'Subscribe', url: 'https://sov.ai/home', new_tab: false },
    ],
  },
];

export default function Footer() {
  return (
    /*
     * Flat ground with a single hairline top rule, replacing the navy vertical
     * gradient. Type is compact — the columns previously ran to text-xl, which
     * gave a utility footer the weight of body copy.
     */
    <footer className="w-full bg-ground border-t border-hairline">
      <div className="mx-auto w-10/12 sm:w-11/12 lg:w-4/5 max-w-screen-2xl pt-10 pb-4">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-y-8">
          <div className="sm:w-1/4 flex flex-col items-start gap-y-3">
            <img src={SovAILogo} alt="SovAI" className="object-cover w-28" />
            <p className="m-0 text-muted text-[13px] leading-relaxed max-w-[28ch]">
              Python based solution for advanced investment management
            </p>
          </div>

          {linkData.map(column => (
            <div key={column.title} className="flex flex-col gap-y-3">
              <h2 className={`m-0 ${COLUMN_HEADING}`}>{column.title}</h2>
              {/* These were <div onClick> — unreachable by Tab and unannounced
                  as links. Internal destinations are router <Link>s, external
                  ones real <a href>s, and scroll actions <button>s. */}
              <ul className="list-none m-0 p-0 flex flex-col gap-y-2">
                {column.links.map(item => {
                  const isExternal = /^https?:\/\//.test(item.url || '');

                  return (
                    <li key={item.name}>
                      {item.action ? (
                        <button
                          type="button"
                          className={`${FOOTER_LINK} bg-transparent border-0 p-0 cursor-pointer`}
                          onClick={item.action}
                        >
                          {item.name}
                        </button>
                      ) : isExternal ? (
                        <a
                          href={item.url}
                          className={FOOTER_LINK}
                          {...(item.new_tab
                            ? { target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link to={item.url || '/'} className={FOOTER_LINK}>
                          {item.name}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div className="flex flex-col gap-y-3">
            <h2 className={`m-0 ${COLUMN_HEADING}`}>Updates</h2>
            {/* Was a gradient pill. Now the same flat bordered control the
                header uses. */}
            <a
              href="https://blog.sov.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className={
                'inline-flex items-center justify-center px-4 py-[7px] border border-hairline-strong ' +
                'no-underline font-mono text-[13px] lowercase tracking-[0.08em] text-ink ' +
                `hover:border-accent hover:text-accent rounded-sharp ${FOCUS_RING}`
              }
            >
              blog sign-up
            </a>
          </div>
        </div>

        <div className="mt-10 pt-4 border-t border-hairline flex flex-col sm:flex-row gap-y-2 sm:items-center">
          <span className="font-mono text-[12px] text-muted">
            ©2025 Sovai. All rights reserved.
          </span>
          <div className="grow" />
          <div className="flex flex-row gap-x-6">
            <a
              href="https://land.sov.ai/privacy-policy"
              className={FOOTER_LINK}
            >
              Privacy Policy
            </a>
            <a
              href="https://land.sov.ai/terms-of-service"
              className={FOOTER_LINK}
            >
              Terms &amp; Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
